import type { ProductConfigBuilder } from "../product-types";
import { DECO, breadcrumb } from "./shared";
import remote from "@/assets/products/tv5/remote-clean.jpg";
import hero from "@/assets/products/tv5-ultra/hero.jpg";
import performanceOpaque from "@/assets/products/tv5-ultra/performance-opaque.jpg";
import performanceTransparent from "@/assets/products/tv5-ultra/performance-transparent.jpg";
import ports from "@/assets/products/tv5-ultra/ports.jpg";
import softwareLifestyle from "@/assets/products/tv5-ultra/software-lifestyle.jpg";

export const buildTv5UltraConfig: ProductConfigBuilder = (ctx) => {
  const m = ctx.messages;
  const u = ctx.urls;

  return {
    breadcrumb: breadcrumb(ctx, "inext TV5 Ultra"),
    sections: [
      {
        kind: "hero",
        bgClassName: "bg-muted/10",
        decorations: [{ kind: "grid", className: DECO.grid25 }],
        primaryGlow: false,
        eyebrow: m.tv5UltraHeroEyebrow,
        headline: m.tv5UltraHeroHeadline,
        sub: m.tv5UltraHeroSub,
        contactHref: u.contactSales,
        contactLabel: m.tv6UltraContactSales,
        distributorHref: u.distributors,
        distributorLabel: m.tv6UltraFindDistributor,
        compareHref: u.compare,
        compareLabel: m.tv5UltraCompareLabel,
        media: {
          type: "image",
          src: hero,
          alt: m.tv5UltraHeroImageAlt,
          width: 1200,
          height: 799,
          loading: "eager",
          shadowGlow: true,
        },
      },
      {
        kind: "glance",
        bgClassName: "bg-card/50",
        stats: [
          [m.tv5UltraGlanceValue1, m.tv5UltraGlanceLabel1],
          [m.tv5UltraGlanceValue2, m.tv5UltraGlanceLabel2],
          [m.tv5UltraGlanceValue3, m.tv5UltraGlanceLabel3],
          [m.tv5UltraGlanceValue4, m.tv5UltraGlanceLabel4],
        ],
      },
      {
        kind: "intro",
        name: "TV5 Ultra",
        paras: [m.tv5UltraIntroPara1, m.tv5UltraIntroPara2],
      },
      {
        kind: "performance",
        bgClassName: "bg-muted/15",
        layout: "imageRight",
        eyebrow: m.tv6UltraV2PerformanceEyebrow,
        title: m.tv5UltraPerformanceTitle,
        body: m.tv5UltraPerformanceBody,
        accent: { style: "chips", value: m.tv5UltraPerformanceAccent },
        media: {
          type: "reveal",
          opaque: performanceOpaque,
          transparent: performanceTransparent,
          width: 1200,
          height: 800,
          label: m.tv5UltraHardwareRevealLabel,
          alt: m.tv5UltraHardwareRevealImageAlt,
        },
      },
      {
        kind: "connectivity",
        decorations: [{ kind: "grid", className: DECO.grid20 }],
        layout: "imageLeft",
        eyebrow: m.tv6UltraV2ConnectivityEyebrow,
        title: m.tv5UltraConnectivityTitle,
        caption: { style: "list", value: m.tv5UltraConnectivityCaption },
        media: {
          type: "image",
          src: ports,
          alt: m.tv5UltraPortsImageAlt,
          width: 1200,
          height: 799,
          loading: "lazy",
        },
      },
      {
        kind: "software",
        bgClassName: "bg-muted/20",
        layout: "imageRight",
        eyebrow: m.tv6UltraV2SoftwareEyebrow,
        title: m.tv5UltraSoftwareTitle,
        body: m.tv5UltraSoftwareBody,
        cards: [
          [m.tv6UltraV2SoftwareCustomTitle1, m.tv6UltraV2SoftwareCustomBody1],
          [m.tv6UltraV2SoftwareCustomTitle2, m.tv6UltraV2SoftwareCustomBody2],
          [m.tv6UltraV2SoftwareCustomTitle3, m.tv6UltraV2SoftwareCustomBody3],
        ],
        media: {
          type: "image",
          src: softwareLifestyle,
          alt: m.tv5UltraSoftwareImageAlt,
          width: 1200,
          height: 675,
          loading: "lazy",
          shadowGlow: true,
        },
      },
      {
        kind: "remote",
        decorations: [{ kind: "primaryBlob", className: DECO.remotePrimaryBlob }],
        layout: "imageLeft",
        eyebrow: m.tv6RemoteEyebrow,
        title: m.tv5UltraRemoteTitle,
        body: m.tv5UltraRemoteBody,
        bullets: [m.tv5UltraRemoteFeature1, m.tv5UltraRemoteFeature2, m.tv5UltraRemoteFeature3],
        appLink: { href: u.remoteApp, label: m.remoteAppProductCallout },
        note: {
          title: m.tv5RemoteIrNoteTitle,
          body: m.tv5RemoteIrNoteBody,
          link: {
            href: `${u.accessories}?category=remotes#accessory-list`,
            label: m.tv6RemoteBtNoteLink,
          },
        },
        media: {
          type: "image",
          src: remote,
          alt: m.tv5RemoteImageAlt,
          width: 1000,
          height: 1000,
          loading: "lazy",
          maxWidth: true,
        },
      },
      {
        kind: "alcatraz",
        pill: m.operationsStatPill,
        title: m.tv6UltraAlcatrazTitle,
        body: m.tv5UltraAlcatrazBody,
        ctaHref: u.alcatraz,
        ctaLabel: m.tv6UltraAlcatrazCta,
      },
      {
        kind: "specs",
        bgClassName: "bg-muted/10",
        title: m.tv6UltraV2SpecsTitle,
        note: m.packageNote,
        groups: [
          {
            title: m.tv6UltraV2SpecsGroupHardware,
            rows: [
              [m.tv6UltraSpecChipset, "Allwinner H313"],
              [m.tv6UltraSpecCpu, m.productSpecCpuArmv8Quad],
              [m.tv6UltraSpecGpu, "ARM Mali-G31 MP2"],
              [m.tv6UltraSpecRam, "2 GB DDR3"],
              [m.tv6UltraSpecStorage, "16 GB eMMC"],
              [m.tv6UltraSpecDimensions, "10.5 × 10.5 × 2.0 cm"],
              [m.tv6UltraSpecPower, "5 V / 2 A"],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupConnectivity,
            rows: [
              [m.tv6UltraSpecHdmi, m.productSpecHdmiCableIncluded],
              [m.tv6UltraSpecEthernet, m.productSpecEthernet100],
              [m.tv6UltraSpecWifi, m.productSpecWifiDualBandN],
              [m.tv6UltraSpecUsb, m.productSpecUsbHost],
              [m.tv6UltraSpecCardReader, "microSD / TF"],
              [m.tv6UltraSpecAudioOut, m.productSpecAudioSpdifRcaTrs],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupMedia,
            rows: [
              [m.tv6UltraSpecMaxRes, m.tv6UltraSpecMaxResValue],
              [
                m.tv6UltraSpecVideoCodecs,
                "H.265 (4K@60) · VP9 P2 (4K@30) · H.264 (4K@30) · MPEG-1/2/4 · AVS · WMV9 · VP8 · Xvid/DivX",
              ],
              [
                m.tv6UltraSpecVideoFormats,
                "AVI · MKV · ISO · VOB · DAT · MPG/MPEG · MP4 · MOV · TS · XVID · ASF · FLV · WMV",
              ],
              [
                m.tv6UltraSpecAudioFormats,
                "MP3 · WMA · AAC · WAV · OGG · MIDI · FLAC · APE · ALAC · M4A",
              ],
              [m.tv6UltraSpecImageFormats, "JPG/JPEG · HD JPEG · TIFF · PNG · BMP · GIF"],
              [m.tv6UltraSpec3d, m.tv6UltraSpec3dValue],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupSoftware,
            rows: [
              [m.tv6UltraSpecOs, "Android 10 (AOSP)"],
              [m.tv6UltraSpecDms, "inext Alcatraz™ DMS"],
              [m.tv6UltraSpecTr069, m.tv6UltraSpecTr069Yes],
              [m.tv6UltraSpecFilesystems, "exFAT · FAT32 · NTFS · EXT · HFS+"],
              [m.tv6UltraSpecLanguages, m.tv6UltraSpecLanguagesValue],
              [m.tv6UltraSpecInternet, m.productSpecInternetFeatures],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupPackage,
            rows: [
              ["·", m.tv6PackageRemote],
              ["·", m.tv6UltraPackagePower],
              ["·", m.tv6UltraPackageHdmi],
              ["·", m.tv6UltraPackageBatteries],
              ["·", m.tv6UltraPackageGuide],
            ],
          },
        ],
      },
      {
        kind: "cta",
        title: m.tv5UltraCtaTitle,
        body: m.tv5UltraCtaBody,
        compareHref: u.compare,
        compareLabel: m.tv5UltraCompareLabel,
        contactHref: u.contactSales,
        contactLabel: m.tv6UltraContactSales,
        distributorHref: u.distributors,
        distributorLabel: m.tv6UltraFindDistributor,
      },
    ],
  };
};
